import { FC, useContext, useState } from "react";
import { Box, makeStyles } from "@material-ui/core";
import { includes, last, sortBy } from "lodash";

import { LogInRequiredModal } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { AppContext } from "contexts";
import { useTimer } from "hooks";
import { formatDateRangeCompact } from "lib/format";
import {
  formatCurrencyAmount,
  formatMarketplaceMedianAmountSource,
  formatNumber,
} from "lib/format";
import { parseNumber } from "lib/parse";
import { ResaleInfo } from "services/listings/types";
import {
  LIVE_OFFER_STATES,
  NON_FAILED_DEAL_STATES,
} from "services/offers/constants";
import theme, { Theme } from "theme";

import AAIVerificationModal from "../AAIVerificationModal";
import ConfirmBuyModal from "../ConfirmBuyModal";
import EditBuyNowPriceModal from "../EditBuyNowPriceModal";
import EditMinimumPriceModal from "../EditMinimumPriceModal";
import MakeOfferModal from "../MakeOfferModal";
import MarketplaceMedian from "../MarketplaceMedian";
import SetBuyNowPriceModal from "../SetBuyNowPriceModal";
import VerificationModal from "../VerificationModal";
import LabelValue from "./LabelValue";
import ListingButtons from "./ListingButtons";
import OfferCount from "./OfferCount";
import OwnerLastActive from "./OwnerLastActive";

const formatMultiple = (multiple: number, multiple_source: string) =>
  `${formatNumber(multiple)} x ${formatMarketplaceMedianAmountSource(
    multiple_source,
  )}`;

const useStyles = makeStyles<
  Theme,
  { isAcceptingFinalOffers: boolean; isModal: boolean }
>(() => ({
  thirdRowListingContentContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: theme.spacing(2),
    gap: `${theme.spacing(2)}px`,
    [theme.breakpoints.up("md")]: {
      width: "50%",
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: 0,
      paddingTop: theme.spacing(2),
    },
  },
  thirdRowColumnsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "100%",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "space-around",
      alignItems: props => (props.isAcceptingFinalOffers ? "flex-start" : ""),
      paddingLeft: 0,
      maxWidth: "100%",
    },
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      height: "100%",
      gap: `${theme.spacing(2)}px`,
    },
  },
  thirdRowColumn: {
    paddingRight: theme.spacing(2),
    height: "100%",
    width: "100%",
    maxWidth: "300px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-evenly",
    "&:last-child": {
      paddingRight: theme.spacing(0),
    },
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      gap: `${theme.spacing(2)}px`,
      alignItems: props => (props.isAcceptingFinalOffers ? "center" : ""),
    },
  },
  singleColumn: {
    maxWidth: "300px",
    width: "100%",
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
      alignItems: "center",
      textAlign: "center",
    },
  },
  thirdRowButtonsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
  },
}));

interface ListingContentProps {
  isModal: boolean;
  listingId: number;
}

const ListingContent: FC<ListingContentProps> = ({ isModal, listingId }) => {
  const currentTime = useTimer();
  const { user } = useContext(AppContext);
  const {
    asset,
    listing,
    proxy,
    valuation,
    handleRefreshListing,
  } = useAssetDetailData(listingId);

  const { resale_info } = asset;
  const resaleInfo =
    "last_update" in (resale_info ?? {}) ? (resale_info as ResaleInfo) : null;
  const {
    accept_final_offers,
    accepting_final_offers_end_at,
    buy_now_price,
    buy_now_price_multiple,
    buy_now_price_multiple_source,
    deal,
    is_owner,
    marketplace_median,
    marketplace_median_multiplier,
    marketplace_median_source,
    minimum_price,
    offers,
    owner_last_active_date,
    proxy_increment_amount,
    royx_take,
    state,
    term_expiration,
    unique_bidder_count,
  } = listing;
  const {
    dollar_age,
    id: valuationId,
    lifetime_amount,
    ltm,
    track_list,
  } = valuation;

  const isOpenListing = state === "open";
  const isAcceptingFinalOffers = state === "accepting_final_offers";
  const classes = useStyles({ isAcceptingFinalOffers, isModal });

  const buyerOffers = offers.filter(
    offer =>
      offer.kind !== "seller_counteroffer" ||
      (offer.kind === "seller_counteroffer" &&
        includes(NON_FAILED_DEAL_STATES, offer.state)),
  );
  const sellerCounteroffer = offers.filter(
    offer => offer.kind === "seller_counteroffer",
  );
  const highestOpenSellerCounterofferAccepted =
    isAcceptingFinalOffers &&
    sellerCounteroffer &&
    last(
      sortBy(
        sellerCounteroffer.filter(offer =>
          LIVE_OFFER_STATES.includes(offer.state),
        ),
        offer => Number(offer.amount),
      ),
    );
  const highestOpenBuyerOffer =
    buyerOffers &&
    last(
      sortBy(
        buyerOffers.filter(offer => LIVE_OFFER_STATES.includes(offer.state)),
        offer => Number(offer.amount),
      ),
    );
  const highestOpenBuyerOfferAmount = !!highestOpenBuyerOffer
    ? Number(highestOpenBuyerOffer?.amount)
    : !!highestOpenSellerCounterofferAccepted
    ? Number(highestOpenSellerCounterofferAccepted?.amount)
    : 0;
  const uniqueBidderCount = !!listing ? unique_bidder_count : 0;
  const investorOfferCount = !!listing
    ? offers.filter(offer => !includes(["seller_counteroffer"], offer.kind))
        .length
    : 0;

  const dealAmount =
    Number(!!listing && offers.find(offer => offer.id === deal?.id)?.amount) ??
    0;

  const buyNowPrice = buy_now_price ? Number(buy_now_price) : null;
  const marketplaceMedianMultiplier = Number(marketplace_median_multiplier);
  const minimumPrice = Number(minimum_price);
  const royxTake = Number(royx_take);

  const [isSetBuyNowPriceModalOpen, setIsSetBuyNowPriceModalOpen] = useState(
    false,
  );
  const [isEditBuyNowPriceModalOpen, setIsEditBuyNowPriceModalOpen] = useState(
    false,
  );
  const [isLogInRequiredModalOpen, setIsLogInRequiredModalOpen] = useState(
    false,
  );
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isConfirmBuyModalOpen, setIsConfirmBuyModalOpen] = useState(false);
  const [isMakeOfferModalOpen, setIsMakeOfferModalOpen] = useState(false);
  const [
    isEditMinimumPriceModalOpen,
    setIsEditMinimumPriceModalOpen,
  ] = useState(false);
  const [isAAIVerificationModalOpen, setIsAAIVerificationModalOpen] = useState(
    false,
  );
  const userActiveOffer = offers.filter(
    offer =>
      offer.buyer_id === user?.userId &&
      LIVE_OFFER_STATES.includes(offer.state),
  )[0];

  const handleOpenEditBuyNowPriceModal = async () => {
    handleRefreshListing();
    setIsEditBuyNowPriceModalOpen(true);
  };

  return (
    <>
      {isAAIVerificationModalOpen && (
        <AAIVerificationModal
          onClose={() => setIsAAIVerificationModalOpen(false)}
        />
      )}
      {isSetBuyNowPriceModalOpen && (
        <SetBuyNowPriceModal
          onClose={() => setIsSetBuyNowPriceModalOpen(false)}
          open
          buyNowPrice={buyNowPrice}
          dollarAge={dollar_age}
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          isAcceptingFinalOffersEnabled={accept_final_offers}
          isMarketplaceMedianDisabled={!marketplace_median}
          listingId={listingId}
          lte={lifetime_amount}
          ltm={ltm}
          marketplaceMedian={marketplace_median}
          marketplaceMedianMultiplier={marketplaceMedianMultiplier}
          marketplaceMedianSource={marketplace_median_source}
          minimumPrice={minimumPrice}
          offers={offers}
          resaleInfo={resaleInfo}
          royxTake={royxTake}
          termExpirationDate={term_expiration}
          trackList={track_list}
        />
      )}
      {isEditBuyNowPriceModalOpen && (
        <EditBuyNowPriceModal
          open={isEditBuyNowPriceModalOpen}
          onClose={() => setIsEditBuyNowPriceModalOpen(false)}
          marketplaceMedianSource={marketplace_median_source}
          marketplaceMedianMultiplier={marketplaceMedianMultiplier}
          marketplaceMedian={marketplace_median}
          buyNowPrice={buyNowPrice}
          dollarAge={dollar_age}
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          isAcceptingFinalOffersEnabled={accept_final_offers}
          isMarketplaceMedianDisabled={!marketplace_median}
          listingId={listingId}
          lte={lifetime_amount}
          ltm={ltm}
          minimumPrice={minimumPrice}
          offers={offers}
          resaleInfo={resaleInfo}
          royxTake={royxTake}
          termExpirationDate={term_expiration}
          trackList={track_list}
        />
      )}
      {isLogInRequiredModalOpen && (
        <LogInRequiredModal
          open
          onClose={() => setIsLogInRequiredModalOpen(false)}
        />
      )}
      {isVerificationModalOpen && (
        <VerificationModal
          open
          onClose={() => setIsVerificationModalOpen(false)}
        />
      )}
      {isConfirmBuyModalOpen && (
        <ConfirmBuyModal
          open
          onClose={() => setIsConfirmBuyModalOpen(false)}
          handleRefreshListing={handleRefreshListing}
          buyNowPrice={buyNowPrice}
          dealId={deal?.id}
          dollarAge={dollar_age}
          listingId={listingId}
          lte={lifetime_amount}
          ltm={ltm}
          offers={offers}
          termExpirationDate={term_expiration}
          trackList={track_list}
          valuationId={valuationId}
          listing={listing}
        />
      )}
      {isMakeOfferModalOpen && (
        <MakeOfferModal
          open
          onClose={() => setIsMakeOfferModalOpen(false)}
          buyNowPrice={buyNowPrice}
          dollarAge={dollar_age}
          highestOpenBuyerOfferAmount={highestOpenBuyerOfferAmount}
          isAcceptingFinalOffers={isAcceptingFinalOffers}
          isAcceptingFinalOffersEnabled={accept_final_offers}
          isMarketplaceMedianDisabled={!marketplace_median}
          listingId={listing.id}
          listingProxyIncrementAmount={parseNumber(
            proxy_increment_amount.toString(),
            2,
          )}
          lte={lifetime_amount}
          ltm={ltm}
          marketplaceMedian={marketplace_median}
          marketplaceMedianMultiplier={marketplaceMedianMultiplier}
          marketplaceMedianSource={marketplace_median_source}
          minimumPrice={minimumPrice}
          offers={offers}
          openProxy={proxy}
          termExpirationDate={term_expiration}
          trackList={track_list}
          userActiveOffer={userActiveOffer}
          valuationId={valuationId}
        />
      )}
      {isEditMinimumPriceModalOpen && (
        <EditMinimumPriceModal
          open={isEditMinimumPriceModalOpen}
          onClose={() => setIsEditMinimumPriceModalOpen(false)}
          listingId={listingId}
          minimumPrice={minimumPrice}
          buyNowPrice={buyNowPrice}
        />
      )}
      <Box className={classes.thirdRowListingContentContainer}>
        <Box className={classes.thirdRowColumnsContainer}>
          {isAcceptingFinalOffers && (
            <>
              <Box className={classes.thirdRowColumn}>
                <LabelValue
                  label="Current&nbsp;Highest&nbsp;Offer"
                  value={
                    highestOpenBuyerOfferAmount
                      ? formatCurrencyAmount("USD", highestOpenBuyerOfferAmount)
                      : EMPTY_VALUE_PLACEHOLDER
                  }
                  secondaryValue={
                    highestOpenBuyerOfferAmount
                      ? formatMultiple(
                          highestOpenBuyerOfferAmount / valuation.ltm,
                          "ltm",
                        )
                      : EMPTY_VALUE_PLACEHOLDER
                  }
                  isModal={isModal}
                />
                <LabelValue
                  label="Time Remaining"
                  value={formatDateRangeCompact(
                    accepting_final_offers_end_at,
                    currentTime,
                  )}
                  isModal={isModal}
                />
                {!is_owner && (
                  <Box className={classes.thirdRowButtonsContainer}>
                    <ListingButtons
                      listingId={listingId}
                      handleOpenEditBuyNowPriceModal={
                        handleOpenEditBuyNowPriceModal
                      }
                      setIsConfirmBuyModalOpen={setIsConfirmBuyModalOpen}
                      setIsEditMinimumPriceModalOpen={
                        setIsEditMinimumPriceModalOpen
                      }
                      setIsLogInRequiredModalOpen={setIsLogInRequiredModalOpen}
                      setIsMakeOfferModalOpen={setIsMakeOfferModalOpen}
                      setIsSetBuyNowPriceModalOpen={
                        setIsSetBuyNowPriceModalOpen
                      }
                      setIsVerificationModalOpen={setIsVerificationModalOpen}
                      setIsAAIVerificationModalOpen={
                        setIsAAIVerificationModalOpen
                      }
                    />
                    <OfferCount
                      investorOfferCount={investorOfferCount}
                      uniqueBidderCount={uniqueBidderCount}
                    />
                  </Box>
                )}
              </Box>
            </>
          )}
          {isOpenListing && (
            <Box className={classes.thirdRowColumn}>
              <LabelValue
                label="List Price"
                value={
                  buy_now_price
                    ? formatCurrencyAmount("USD", Number(buy_now_price))
                    : EMPTY_VALUE_PLACEHOLDER
                }
                secondaryValue={
                  buy_now_price_multiple
                    ? formatMultiple(
                        Number(buy_now_price_multiple),
                        buy_now_price_multiple_source,
                      )
                    : EMPTY_VALUE_PLACEHOLDER
                }
                isModal={isModal}
              />
              <LabelValue
                label="Highest Open Offer"
                value={
                  highestOpenBuyerOfferAmount
                    ? formatCurrencyAmount(
                        "USD",
                        Number(highestOpenBuyerOfferAmount),
                      )
                    : EMPTY_VALUE_PLACEHOLDER
                }
                secondaryValue={
                  highestOpenBuyerOfferAmount
                    ? `${formatNumber(
                        highestOpenBuyerOfferAmount / valuation.ltm,
                      )} x ${formatMarketplaceMedianAmountSource("ltm")}`
                    : EMPTY_VALUE_PLACEHOLDER
                }
                isModal={isModal}
              />
              <OwnerLastActive ownerLastActiveDate={owner_last_active_date} />
            </Box>
          )}
          {!isAcceptingFinalOffers && (
            <Box
              className={`${classes.thirdRowColumn} ${
                !isOpenListing ? classes.singleColumn : ""
              }`}
            >
              {!isOpenListing && !isAcceptingFinalOffers && (
                <LabelValue
                  label="Sale Price"
                  value={formatCurrencyAmount("USD", Number(dealAmount))}
                  secondaryValue={formatMultiple(
                    dealAmount / valuation.ltm,
                    "ltm",
                  )}
                  isModal={isModal}
                />
              )}
              {!isAcceptingFinalOffers && (
                <>
                  <ListingButtons
                    listingId={listingId}
                    handleOpenEditBuyNowPriceModal={
                      handleOpenEditBuyNowPriceModal
                    }
                    setIsConfirmBuyModalOpen={setIsConfirmBuyModalOpen}
                    setIsEditMinimumPriceModalOpen={
                      setIsEditMinimumPriceModalOpen
                    }
                    setIsLogInRequiredModalOpen={setIsLogInRequiredModalOpen}
                    setIsMakeOfferModalOpen={setIsMakeOfferModalOpen}
                    setIsSetBuyNowPriceModalOpen={setIsSetBuyNowPriceModalOpen}
                    setIsVerificationModalOpen={setIsVerificationModalOpen}
                    setIsAAIVerificationModalOpen={
                      setIsAAIVerificationModalOpen
                    }
                  />
                  <OfferCount
                    investorOfferCount={investorOfferCount}
                    uniqueBidderCount={uniqueBidderCount}
                  />
                  <Box
                    className={classes.acceptingFinalOffersMarketplaceMedian}
                  >
                    <MarketplaceMedian
                      marketplaceMedian={marketplace_median}
                      marketplaceMedianMultiplier={Number(
                        marketplace_median_multiplier,
                      )}
                      marketplaceMedianSource={marketplace_median_source}
                    />
                  </Box>
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ListingContent;
